/**
 * Shared formatting utilities for consistent data display across the app
 */

/**
 * Format a number as USD currency
 * @param {number} amount - The amount to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.compact - Use compact notation for large numbers
 * @param {boolean} options.showCents - Show cents even for whole numbers (default: true)
 * @param {string} options.currency - ISO currency code (default: 'USD')
 * @param {string} options.locale - Locale string (default: 'en-US')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, options = {}) => {
  const {
    compact = false,
    showCents = true,
    currency = 'USD',
    locale = 'en-US'
  } = options;
  const numeric = Number(amount);
  const value = Number.isFinite(numeric) ? numeric : 0;
  const minimumFractionDigits = showCents ? 2 : 0;
  const maximumFractionDigits = showCents ? 2 : 0;
  
  if (compact && Math.abs(value) >= 1000) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
  }
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits
  }).format(value);
};

/**
 * Format a date for display
 * @param {string|Date} dateValue - The date to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeTime - Include time in output
 * @param {boolean} options.shortMonth - Use short month names
 * @param {boolean} options.includeYear - Include year in output (default: true)
 * @returns {string} Formatted date string
 */
export const formatDate = (dateValue, options = {}) => {
  if (!dateValue) return 'N/A';
  
  const { includeTime = false, shortMonth = true, includeYear = true } = options;
  
  try {
    // FIX BUG-007: Dates stored as UTC midnight (e.g. "2025-03-15T00:00:00.000Z") shift
    // back one day in western timezones. Two cases to handle:
    // 1. Date-only strings "YYYY-MM-DD" → parse as local time
    // 2. UTC midnight ISO strings → format in UTC to preserve the intended date
    let normalizedValue = dateValue;
    let useUTC = false;
    if (typeof dateValue === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        // Case 1: bare date string — parse as local time
        normalizedValue = dateValue + 'T00:00:00';
      } else if (/T00:00:00(\.000)?Z$/.test(dateValue)) {
        // Case 2: UTC midnight — this is a "date-only" value, format in UTC
        useUTC = true;
      }
    }
    const date = new Date(normalizedValue);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    const formatOptions = {
      month: shortMonth ? 'short' : 'long',
      day: 'numeric',
      ...(includeYear && { year: 'numeric' }),
      ...(includeTime && { hour: 'numeric', minute: '2-digit' }),
      ...(useUTC && !includeTime && { timeZone: 'UTC' })
    };
    
    return date.toLocaleDateString('en-US', formatOptions);
  } catch {
    return 'Invalid Date';
  }
};

/**
 * Format a date and time for display
 * @param {string|Date} dateValue - The date to format
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (dateValue) => {
  return formatDate(dateValue, { includeTime: true });
};

/**
 * Safe drop-in replacement for `new Date(x).toLocaleDateString(locale, opts)`.
 * Handles UTC midnight dates from the backend that would otherwise shift back
 * one day in western timezones.
 * @param {string|Date} dateValue - The date to format
 * @param {string} [locale='en-US'] - Locale string
 * @param {Intl.DateTimeFormatOptions} [opts] - Format options
 * @returns {string} Formatted date string
 */
export const safeLocaleDateString = (dateValue, locale = 'en-US', opts = {}) => {
  if (!dateValue) return '';
  try {
    let useUTC = false;
    let normalizedValue = dateValue;
    if (typeof dateValue === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        normalizedValue = dateValue + 'T00:00:00';
      } else if (/T00:00:00(\.000)?Z$/.test(dateValue)) {
        useUTC = true;
      }
    }
    const date = new Date(normalizedValue);
    if (isNaN(date.getTime())) return 'Invalid Date';
    const finalOpts = useUTC && !opts.hour ? { ...opts, timeZone: 'UTC' } : opts;
    return date.toLocaleDateString(locale, finalOpts);
  } catch {
    return 'Invalid Date';
  }
};

/**
 * Format a relative time (e.g., "2 days ago")
 * @param {string|Date} dateValue - The date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (dateValue) => {
  if (!dateValue) return 'N/A';
  
  try {
    // FIX BUG-007: Same date normalization as formatDate
    let normalizedValue = dateValue;
    if (typeof dateValue === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        normalizedValue = dateValue + 'T00:00:00';
      } else if (/T00:00:00(\.000)?Z$/.test(dateValue)) {
        // UTC midnight = date-only value; parse as local noon to avoid day shift
        normalizedValue = dateValue.replace(/T00:00:00(\.000)?Z$/, 'T12:00:00');
      }
    }
    const date = new Date(normalizedValue);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    
    // Handle future dates - show "in X time" instead of negative values
    if (diffMs < 0) {
      const futureDiffMs = Math.abs(diffMs);
      const futureSeconds = Math.floor(futureDiffMs / 1000);
      const futureMinutes = Math.floor(futureSeconds / 60);
      const futureHours = Math.floor(futureMinutes / 60);
      const futureDays = Math.floor(futureHours / 24);
      
      if (futureSeconds < 60) return 'in a moment';
      if (futureMinutes < 60) return `in ${futureMinutes} minute${futureMinutes !== 1 ? 's' : ''}`;
      if (futureHours < 24) return `in ${futureHours} hour${futureHours !== 1 ? 's' : ''}`;
      if (futureDays < 7) return `in ${futureDays} day${futureDays !== 1 ? 's' : ''}`;
      return formatDate(dateValue);
    }
    
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSeconds < 60) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return formatDate(dateValue);
  } catch {
    return 'Invalid Date';
  }
};

/**
 * Format a percentage value
 * @param {number} value - The percentage value
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  const num = Number(value) || 0;
  return `${num.toFixed(decimals)}%`;
};

/**
 * Format a number with thousand separators
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 0) => {
  const num = Number(value) || 0;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num);
};

/**
 * Format mileage with 'mi' suffix
 * @param {number} miles - The mileage to format
 * @returns {string} Formatted mileage string
 */
export const formatMileage = (miles) => {
  const num = Number(miles) || 0;
  return `${formatNumber(Math.round(num))} mi`;
};

/**
 * Format duration in days
 * @param {number} days - Number of days
 * @returns {string} Formatted duration string
 */
export const formatDuration = (days) => {
  const num = Number(days) || 0;
  if (num === 1) return '1 day';
  return `${num} days`;
};

/**
 * Format a snake_case or camelCase label to Title Case
 * @param {string} label - The label to format
 * @returns {string} Formatted label
 */
export const formatLabel = (label) => {
  if (!label) return '';
  return label
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Format phone number for display
 * @param {string} phone - Phone number
 * @returns {string} Formatted phone number
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

export default {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatRelativeTime,
  formatPercentage,
  formatNumber,
  formatMileage,
  formatDuration,
  formatLabel,
  truncateText,
  formatPhone
};
